import React, { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import {
    PropertiesPanelLookProvider,
    panel,
    PropertiesPanelPicker,
    PropertiesPanelPickerOption,
    PropertiesPanelSwatch,
    scenePanelPalette
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { thinScrollbar } from '../../../../../widgets/scrollbar';
import { LEFT_BY_KEYBOARD, leaveInlineField, rearmInlineField } from '../inlineNameField';
import { useDropdown } from '../../../../../widgets/hooks/useDropdown';
import { SelectedIcon, EmptyNodeIcon, ChevronIcon, CloseIcon, GroupIcon, MaterialsIcon, RevealIcon } from '../../../../../widgets/icons/scene';
import {
    CamerasRailIcon as CamerasIcon,
    EffectsRailIcon as EffectsIcon,
    FreeCameraIcon,
    LightsRailIcon as LightsIcon,
    ObjectsRailIcon as ObjectIcon,
    WorldRailIcon as WorldIcon
} from './sceneRailIcons';

// L'INVOLUCRO DELL'INSPECTOR, UNO SOLO. Prima ce n'erano quattro — uno per genere di
// soggetto — e ognuno portava il suo contenitore, il suo titolo e un `createGlobalStyle` che
// inchiodava gli input a 80px con `!important`. In una colonna che si trascina quel vincolo è
// esattamente quello sbagliato: il campo non può crescere con la colonna.
//
// Qui resta una cosa sola: **la testata dice CHI è il soggetto**, non di che genere è il
// pannello. Marca, nome — che si modifica dove si legge — specifiche, «mostramelo» e chiudi.
// Sotto, le proprietà: quelle le costruiscono gli stessi `use*PropertiesCreator` di prima,
// che è la parte buona dei quattro overlay e non si tocca.
//
// La griglia delle proprietà non è qui: è già in `PropertiesPanelProperty` — due colonne
// `1fr 1fr` con il campo a `flex: 1` — e funzionava. Bastava smettere di combatterla.

// `selection` non è una specie come le altre: è il caso in cui il soggetto sono TANTI. Sta
// nella stessa famiglia perché la testata è la stessa — una marca, un nome, e sotto il corpo —
// e perché il pannello che descrive uno solo è il caso particolare, non il contrario.
/**
 * `camera` è UNA camera, `cameras` sono le impostazioni che valgono per TUTTE. La differenza non è
 * pignoleria: campo visivo, zoom, limiti e rotazione automatica il motore li tiene sulla scena, non
 * sulla singola camera, e un pannello che li mostrasse col marchio «Camera» prometterebbe che sono
 * di quella lì — otto camere mostrerebbero otto copie dello stesso numero.
 */
export type InspectorSubjectKind = 'object' | 'empty' | 'light' | 'camera' | 'cameras' | 'material' | 'matgroup' | 'world' | 'effects' | 'selection';

// LE SEZIONI PORTANO LO STESSO GLIFO DEL RAIL: oggetto, luce, camera, mondo ed effetti sono le
// icone Material con cui il rail le nomina. Gli altri — empty, gruppo, selezione — restano i glifi
// della scena, che non hanno una voce nel rail.
const KIND_GLYPH: Record<InspectorSubjectKind, ReactNode> = {
    object: <ObjectIcon />,
    empty: <EmptyNodeIcon />,
    light: <LightsIcon />,
    camera: <CamerasIcon />,
    // Il pannello «Free camera» della sezione Cameras: la vista libera, non una camera salvata.
    cameras: <FreeCameraIcon />,
    material: <MaterialsIcon />,
    matgroup: <GroupIcon />,
    world: <WorldIcon />,
    effects: <EffectsIcon />,
    selection: <SelectedIcon />
};

/**
 * I comandi che appartengono alla COLONNA, non al soggetto: chiuderla è una cosa che si fa
 * all'Inspector, non all'oggetto che ci sta dentro. Li offre chi possiede la colonna; dove la
 * colonna non c'è — un overlay, una storia di Storybook — il bottone non compare.
 */
interface InspectorChrome {
    onCollapse?: () => void;
}

const InspectorChromeContext = createContext<InspectorChrome>({});

export const InspectorChromeProvider = InspectorChromeContext.Provider;

// QUI NASCONO I COLORI DEL PANNELLO. Sotto questo nodo ogni componente li trova; fuori di qui
// non esistono, e i componenti ripiegano sui valori chiari — è così che le pagine che usano gli
// stessi controlli fuori dalla scena restano esattamente come erano.
const Shell = styled.div`
    ${scenePanelPalette}

    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
`;

// L'ALTEZZA DELLA TESTATA È DICHIARATA, non dedotta da ciò che ci finisce dentro. Il pannello di
// un materiale porta pastiglie nel sottotitolo e riempie i sessantasei; quello di un oggetto no,
// e resta centrato negli stessi sessantasei. Senza il numero, passare da una mesh al suo
// materiale farebbe saltare in su tutto il pannello.
const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 56px;
    padding: 8px;
    border-bottom: 1px solid ${panel.line};
`;

// QUARANTADUE E NON TRENTADUE, e non è estetica: una marca più BASSA della colonna del nome si
// centra dentro di essa, quindi si stacca dal bordo alto della testata più di quanto si stacchi
// dal lato. Alta quanto la colonna, il suo bordo cade esattamente sul rientro.
//
// L'ANELLO È INTERNO e non un bordo: un bordo aggiungerebbe due pixel alla misura, e il riquadro
// smetterebbe di essere quarantadue.
const Glyph = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px ${panel.line};
    color: ${panel.inkMuted};
    background: ${panel.ground};
    overflow: hidden;

    svg {
        width: 18px;
        height: 18px;
    }

    /* I glifi Material si riempiono, quelli della scena sono disegnati a contorno: riempire questi
       cancellerebbe il tratto. */
    svg:not([stroke]) {
        fill: currentColor;
    }
`;

const Identity = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

// LA MARCA DEL SOGGETTO NON STA SOPRA IL NOME: sta accanto alle sue misure, come pastiglia.
// Scritta sopra — «LIGHT», «MATERIAL» — rubava il primo rigo alla cosa che si sta guardando; qui
// resta leggibile e lascia il primo rigo al nome, che è quello che si cerca.
const KIND_LABEL: Record<InspectorSubjectKind, string> = {
    object: T._('Object', 'Admin'),
    empty: T._('Empty', 'Admin'),
    light: T._('Light', 'Admin'),
    camera: T._('Camera', 'Admin'),
    cameras: T._('Cameras', 'Admin'),
    material: T._('Material', 'Admin'),
    matgroup: T._('Material group', 'Admin'),
    world: T._('World', 'Admin'),
    effects: T._('Effects', 'Admin'),
    selection: T._('Selection', 'Admin')
};

const Meta = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

// Il nome prende la riga; se accanto c'è uno stato da dire, quello si prende il posto che gli
// serve e il campo si stringe di conseguenza.
const NameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

// LA PASTIGLIA PORTA LA TINTA DELLA SPECIE, e la parola il suo inchiostro (vedi `KIND_INK`). Chi una tinta non ce l'ha — una vista, non
// un soggetto — prende il fondo incassato e un filo intorno, che è la pastiglia muta di sempre.
// LE TRE PRIME TINTE VENGONO DAL PROTOTIPO — il soggetto, il gruppo, la cosa che si applica. Le
// due nuove seguono lo stesso criterio: la luce prende l'ambra con cui il prototipo tinge già la
// lampadina nelle liste, la camera un blu ardesia che nessun altro ruolo usa. World ed Effects
// restano senza: non sono soggetti, sono viste — e una tinta direbbe che c'è una specie da
// riconoscere dove invece la pagina è una sola.
const KIND_TINT: Partial<Record<InspectorSubjectKind, string>> = {
    object: panel.kindSubject,
    // UN EMPTY RAGGRUPPA E BASTA, quindi porta il viola del raggruppamento e non il verde-acqua
    // del soggetto: la tinta dice il RUOLO, e il suo è tenere insieme, non portare proprietà.
    empty: panel.kindGroup,
    material: panel.kindAsset,
    light: panel.kindLight,
    camera: panel.kindCamera,
    // `cameras` NON HA TINTA, come `world` e `effects`: la tinta dice la specie di un soggetto, e
    // un capitolo della scena non è un soggetto.
    matgroup: panel.kindGroup,
    selection: panel.kindGroup
};

// LA PAROLA HA IL COLORE DELLA SPECIE, come le pastiglie del Composer: lavato sotto, inchiostro
// pieno della stessa tinta sopra, in maiuscolo. I due editor scrivono la specie nello stesso modo.
const KIND_INK: Partial<Record<InspectorSubjectKind, string>> = {
    object: panel.kindSubjectInk,
    empty: panel.kindGroupInk,
    material: panel.kindAssetInk,
    light: panel.kindLightInk,
    camera: panel.kindCameraInk,
    matgroup: panel.kindGroupInk,
    selection: panel.kindGroupInk
};

const Badge = styled.span<{ $tint?: string; $ink?: string }>`
    display: inline-flex;
    align-items: center;
    flex: none;
    height: 15px;
    padding: 0 6px;
    border-radius: 999px;
    background: ${(props) => props.$tint ?? panel.ground};
    box-shadow: ${(props) => (props.$tint ? 'none' : `inset 0 0 0 1px ${panel.line}`)};
    color: ${(props) => props.$ink ?? (props.$tint ? panel.ink : panel.inkMuted)};
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    white-space: nowrap;
`;

// «A QUALI OGGETTI SEI ASSEGNATO» — la pastiglia che porta un conto e lo apre.
//
// Ha la misura e la forma della pastiglia della specie, ed è voluto: sono due dati dello stesso
// soggetto scritti sulla stessa riga, e uno più grande dell'altro direbbe che conta di più. A
// distinguerle è che questa RISPONDE — porta il chevron, e sotto ha le righe di ciò che conta.
//
// IL TONO È QUELLO DELLA SPECIE CHE CONTA, non un blu «informativo»: «4 objects» conta oggetti e
// veste il verde-acqua degli oggetti, lo stesso che portano nell'albero e nella pastiglia del loro
// pannello. Un tono unico per «informazione» direbbe soltanto che è un dato — di che cosa parli,
// lo direbbe solo la parola.
//
// VUOTA E NON PIENA: la pastiglia della specie porta il lavato come FONDO perché ci sta scritta
// sopra una parola sola e nera; qui la parola è del colore della specie, e un lavato sotto una
// parola satura della stessa tinta è una macchia. Contorno, quindi — e il lavato torna come fondo
// nel momento in cui ci si passa sopra, che è quando la pastiglia dice di essere un bersaglio.
const Tag = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    position: relative;
    flex: none;
    height: 15px;
    padding: 0 6px;
    border: 0;
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px ${panel.kindSubjectInk};
    background: transparent;
    color: ${panel.kindSubjectInk};
    font-family: inherit;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;

    /* IL BERSAGLIO È PIÙ GRANDE DEL DISEGNO. Quindici è la misura della riga dei metadati —
       portare la pastiglia a ventiquattro vorrebbe dire alzare la testata intera per farle
       posto — e ventiquattro è il pavimento sotto cui un bersaglio non si colpisce più (WCAG
       2.5.8). L'area che risponde al clic la allarga uno pseudo-elemento: non occupa spazio,
       quindi non sposta niente. */
    &::after {
        content: '';
        position: absolute;
        inset: -5px -2px;
    }

    &:hover,
    &:focus-visible {
        background: ${panel.kindSubject};
        outline: none;
    }

    /* IL CHEVRON GUARDA IN GIÙ QUANDO È CHIUSO — dove sta la cosa che si apre — e in su quando
       è aperto: il glifo nasce rivolto a destra, quindi la posizione di riposo è già una
       rotazione. */
    svg {
        width: 8px;
        height: 8px;
        transform: rotate(90deg);
        transition: transform 130ms ease;
    }

    &[aria-expanded='true'] svg {
        transform: rotate(-90deg);
    }
`;

/**
 * Il conto e le voci che lo compongono. La parola la scrive chi conta — è l'unico a sapere se il
 * singolare è «object» o «light» — e ZERO VOCI NON SI PASSANO: «0 objects» è la risposta a una
 * domanda che nessuno ha fatto, e una maniglia che apre il vuoto è una promessa falsa.
 */
export interface InspectorUsage {
    /** Quello che si legge sulla pastiglia: «4 objects». */
    label: string;
    /** Le righe che si aprono sotto. */
    entries: PropertiesPanelPickerOption[];
    /** Quella di adesso si accende, e non si cerca. */
    current?: string | number;
    /** Scegliere una riga vuol dire ANDARCI: qui si esce dal soggetto di questo pannello. */
    onPick: (value: string | number) => void;
}

// OGNI PASTIGLIA APRE LA PROPRIA, ed è il motivo per cui è un componente e non un pezzo di JSX
// dentro la testata: la tendina è uno stato, e uno stato per pastiglia si tiene solo dandole un
// componente suo. Con un solo stato in comune, aprire «objects» aprirebbe anche «actions».
const UsageTag: FC<{ usage: InspectorUsage }> = ({ usage }) => {
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();

    return (
        <>
            <Tag type='button' aria-expanded={isOpen} onClick={(event) => (isOpen ? closeDropdown() : openDropdown(event.currentTarget, 'bottom-start'))}>
                {usage.label}
                <ChevronIcon aria-hidden='true' />
            </Tag>
            {isOpen && (
                <Dropdown>
                    <PropertiesPanelPicker
                        search={false}
                        options={usage.entries}
                        current={usage.current}
                        onSelect={(value) => {
                            closeDropdown();
                            usage.onPick(value);
                        }}
                    />
                </Dropdown>
            )}
        </>
    );
};

// Il nome si modifica dove si legge: nessun bottone «rinomina», nessuna finestra. Quando non
// è modificabile resta lo stesso testo, senza cornice e senza cursore che promette altro.
// IL CAMPO RESTITUISCE LO SPAZIO CHE LA SUA CORNICE PORTA: senza il margine negativo il nome
// cadrebbe sei pixel a destra della riga tecnica sotto, e le due partirebbero da due x diverse.
const NameInput = styled.input`
    min-width: 0;
    /* The field takes the row: an input sized by the browser stops short of the panel edge, and the
       frame that appears on hover ended in the middle of the header. */
    flex: 1;
    margin-left: -6px;
    padding: 3px 6px;
    border: 1px solid transparent;
    border-radius: 5px;
    background: transparent;
    color: ${panel.ink};
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    text-overflow: ellipsis;

    /* L'HOVER NON COPRE IL FUOCO: la regola dell'hover pesava di più, e la cornice arancione del
       campo in scrittura compariva solo quando il puntatore se ne andava. Come nel Composer. */
    &:hover:not(:focus):not(:disabled):not(${LEFT_BY_KEYBOARD}) {
        border-color: ${panel.line};
        background: ${panel.field};
    }

    &:focus {
        border-color: ${panel.accent};
        background: ${panel.field};
        outline: none;
    }

    &:disabled {
        color: ${panel.ink};
    }
`;

const Specs = styled.span`
    overflow: hidden;
    color: color-mix(in srgb, ${panel.inkMuted} 78%, transparent);
    font-size: 9px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// I COMANDI STANNO NELL'ANGOLO IN ALTO A DESTRA, e il riferimento è la TESTATA e non la riga del
// nome: dentro la riga si centrerebbero sul nome, che nei due Inspector cade ad altezze diverse.
// Restano nel flusso e non in posizione assoluta, così la loro larghezza la sottraggono da soli
// al nome — che è quello che sa troncarsi.
//
// VENTIQUATTRO E NON DICIOTTO: nel mockup sono più piccoli, ma ventiquattro è il pavimento sotto
// il quale un bersaglio non si colpisce più (WCAG 2.5.8). Il glifo resta della misura del
// disegno; a crescere è solo l'area che risponde al clic.
const HeaderButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    flex: none;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 5px;
    color: ${panel.inkMuted};
    background: transparent;
    cursor: pointer;

    svg {
        width: 12px;
        height: 12px;
    }

    &:hover,
    &:focus-visible {
        color: ${panel.accent};
        background: ${panel.ground};
        outline: none;
    }
`;

// LE CARD SONO BIANCHE SU UN FONDO CHIARO, non il contrario. Con il pannello bianco e le card
// grigie il rapporto era rovesciato: il contenuto sprofondava e i contenitori venivano avanti,
// mentre la cosa da leggere è quello che sta dentro.
// A DESTRA IL RIENTRO LO FA GIÀ LA CORSIA DELLA BARRA. Con dodici pixel di padding su tutti e
// quattro i lati, a destra se ne sommavano dodici più la corsia riservata: le card finivano una
// ventina di pixel prima del bordo e ne avanzavano dodici dall'altra parte — una colonna che
// sembra storta, e venti pixel di larghezza tolti a chi li usa. Qui il rientro destro è la
// corsia e basta, che misura quanto il padding sinistro.
const Body = styled.div`
    flex: 1;
    min-height: 0;
    padding: 12px 0 20px 12px;
    background: ${panel.ground};
    overflow-y: auto;
    ${thinScrollbar}

    /* IL POSTO DELLA BARRA E' SEMPRE RISERVATO. Senza, il contenuto e' largo nove pixel in piu'
       finche' non c'e' da scorrere, e li perde nel momento in cui c'e': aprire un accordion
       spostava di nove pixel tutto quello che stava sopra. */
    scrollbar-gutter: stable;
`;

export interface InspectorShellProps {
    kind: InspectorSubjectKind;
    name: string;
    /** Due o tre cose brevi che dicono com'è fatto: «Mesh · 1.240 vertici». */
    specs?: string;
    /** Le pastiglie che contano qualcosa del soggetto e la aprono: «4 objects». */
    usage?: InspectorUsage[];
    /**
     * GLI STATI CHE SI LEGGONO PRIMA DEL RESTO — «Hidden», «VTO». Stanno accanto al NOME e non
     * fra le specifiche, perché non dicono com'è fatto il soggetto ma come si trova adesso e a
     * che cosa serve: chi apre un pannello e non vede la cosa nel palco deve capire perché senza
     * cercarlo, e chi cerca il nodo su cui vale il finetuning deve riconoscerlo a colpo d'occhio.
     *
     * Più di uno perché sono indipendenti: un nodo può essere nascosto ED essere quello del VTO.
     */
    state?: string | string[];
    /** Assente: il nome si legge e non si tocca. */
    onRename?: (name: string) => void;
    /** «Dove sta?»: fa lampeggiare la sua riga nella colonna di sinistra. */
    onReveal?: () => void;
    /**
     * IL RITRATTO DEL SOGGETTO, quando ne ha uno vero. Un materiale ci mette la propria
     * fotografia — quella che porta anche nella lista — e il glifo generico resta ai soggetti
     * che una faccia non ce l'hanno. Lo passa chi apre il pannello, perché è lui a sapere se
     * quella faccia esiste e che cosa succede premendola.
     */
    glyph?: ReactNode;
    /**
     * L'icona dentro il riquadro, quando non è quella della specie: una selezione multipla porta
     * l'icona della sezione da cui viene — oggetti, luci, camere, materiali — come nel rail.
     */
    icon?: ReactNode;
    children: ReactNode;
}

const InspectorShell: FC<InspectorShellProps> = ({ kind, name, specs, usage, state, onRename, onReveal, glyph, icon, children }) => {
    const { onCollapse } = useContext(InspectorChromeContext);
    const [draft, setDraft] = useState(name);

    // Il nome può cambiare da fuori — l'albero lo rinomina, o si sceglie un altro soggetto —
    // e il campo deve seguirlo invece di restare su quello di prima.
    useEffect(() => setDraft(name), [name]);

    const commit = () => {
        const value = draft.trim();
        if (!value || value === name) {
            setDraft(name);
            return;
        }
        onRename?.(value);
    };

    return (
        <Shell>
            <Header>
                {/* UN MATERIALE SI RICONOSCE DALLA PROPRIA PALLINA, non da un glifo dentro un riquadro:
                    è la stessa marca che porta nella lista, ed è quella che si cerca scorrendo. Se chi
                    apre il pannello ha la fotografia vera, mette quella: la pallina tinta è il ripiego
                    di chi non ce l'ha. */}
                {glyph ??
                    (kind === 'material' ? (
                        <PropertiesPanelSwatch $tint={panel.kindAsset} $size={34} aria-hidden='true' />
                    ) : (
                        <Glyph aria-hidden='true'>{icon ?? KIND_GLYPH[kind]}</Glyph>
                    ))}
                <Identity>
                    <NameRow>
                        <NameInput
                            value={draft}
                            disabled={!onRename}
                            aria-label={T._('Name', 'Admin')}
                            title={name}
                            onChange={(event) => setDraft(event.currentTarget.value)}
                            onBlur={commit}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') leaveInlineField(event.currentTarget);
                                if (event.key === 'Escape') {
                                    setDraft(name);
                                    leaveInlineField(event.currentTarget);
                                }
                            }}
                            onFocus={(event) => rearmInlineField(event.currentTarget)}
                            onMouseLeave={(event) => rearmInlineField(event.currentTarget)}
                        />
                        {(Array.isArray(state) ? state : state ? [state] : []).map((mark) => (
                            <Badge key={mark}>{mark}</Badge>
                        ))}
                    </NameRow>
                    <Meta>
                        <Badge $tint={KIND_TINT[kind]} $ink={KIND_INK[kind]}>
                            {KIND_LABEL[kind]}
                        </Badge>
                        {usage?.map((entry) => (
                            <UsageTag key={entry.label} usage={entry} />
                        ))}
                        {specs && <Specs title={specs}>· {specs}</Specs>}
                    </Meta>
                </Identity>
                {onReveal && (
                    <HeaderButton type='button' onClick={onReveal} aria-label={T._('Show in the list', 'Admin')} title={T._('Show in the list', 'Admin')}>
                        <RevealIcon />
                    </HeaderButton>
                )}
                {onCollapse && (
                    <HeaderButton type='button' onClick={onCollapse} aria-label={T._('Close inspector', 'Admin')} title={T._('Close inspector', 'Admin')}>
                        <CloseIcon />
                    </HeaderButton>
                )}
            </Header>
            {/* QUI PASSA IL CONFINE DELLA SCENA. Le righe di proprietà sono componenti di
                piattaforma, usati anche da preview3D, dalle impostazioni interfaccia e dal Virtual
                Try-On: il disegno nuovo vale per l'Inspector della scena, e questo guscio È
                l'Inspector della scena. Dichiararlo una volta qui evita di passarlo di mano in
                mano per venti righe in sei editor. */}
            <Body>
                <PropertiesPanelLookProvider value='scene'>{children}</PropertiesPanelLookProvider>
            </Body>
        </Shell>
    );
};

export default InspectorShell;
